/**
 * Live per-server recording-status helpers (feat-080-live-server-status, Slice B).
 *
 * The Active Servers board renders a server-pushed `status` plus a live,
 * per-second elapsed-time readout anchored to a **server-authoritative**
 * timestamp (`paused_since` / `last_transcription_at`). Per the spec's
 * single-source-of-truth rule, React performs **no classification** — it only
 * renders the pushed status and counts up from the pushed timestamp.
 *
 * This module holds only the contract-independent, pure pieces:
 *   - `elapsedSeconds(fromTimestamp, now)` — wall-clock seconds since an anchor.
 *   - `formatElapsed(seconds)` — the spec's `m:ss` / `h:mm:ss` readout.
 *
 * The status enum, color/label mapping, and field wiring are added once the
 * architect's ADR locks the WS contract (status values + timestamp field names).
 *
 * Timestamp parsing mirrors `locationTime.ts`: the backend stores UTC and MySQL
 * emits no zone suffix, so a zone-less string is treated as UTC.
 */

import { parseISO } from 'date-fns';

/**
 * Coerce a backend datetime string to a `Date`, treating a zone-less string as
 * UTC (backend stores UTC; MySQL emits no suffix). Strings already carrying `Z`
 * or a numeric offset are left as-is. Mirrors `locationTime.toUtcDate`.
 */
function toUtcDate(dateString: string): Date {
  const hasZone = dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString);
  return parseISO(hasZone ? dateString : `${dateString}Z`);
}

/**
 * Whole wall-clock seconds elapsed between an anchor timestamp and `now`.
 *
 * The anchor is a server-authoritative timestamp (e.g. `paused_since`); `now`
 * is the client's current time (the cosmetic count-up). Returns `null` for
 * empty/unparseable input so callers can omit the readout (graceful
 * degradation, AC7). Never returns a negative value — minor client/server clock
 * skew that puts the anchor slightly in the future clamps to 0 rather than
 * showing a nonsensical negative timer.
 *
 * @param fromTimestamp server-authoritative ISO/MySQL datetime (zone-less → UTC)
 * @param now           reference instant for the count-up (defaults to current time)
 */
export function elapsedSeconds(
  fromTimestamp: string | null | undefined,
  now: Date | number = Date.now(),
): number | null {
  if (!fromTimestamp) return null;
  const anchor = toUtcDate(fromTimestamp);
  if (Number.isNaN(anchor.getTime())) return null;
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const deltaSeconds = Math.floor((nowMs - anchor.getTime()) / 1000);
  return deltaSeconds < 0 ? 0 : deltaSeconds;
}

/**
 * Format whole seconds as the board's elapsed readout.
 *
 * Per the spec examples — "0:08", "3:40", "24:11" — minutes are unpadded and
 * seconds are always two digits (`m:ss`). Once the duration reaches an hour it
 * grows to `h:mm:ss` with minutes zero-padded (e.g. "1:05:09") so a forgotten
 * pause that crosses the hour still reads cleanly.
 *
 * Negative or non-finite input clamps to "0:00".
 */
export function formatElapsed(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const ss = String(secs).padStart(2, '0');
  if (hrs > 0) {
    const mm = String(mins).padStart(2, '0');
    return `${hrs}:${mm}:${ss}`;
  }
  return `${mins}:${ss}`;
}

/**
 * Convenience: the elapsed readout for an anchor timestamp, or `null` when the
 * anchor is absent/unparseable (caller renders nothing — graceful degradation).
 */
export function formatElapsedSince(
  fromTimestamp: string | null | undefined,
  now: Date | number = Date.now(),
): string | null {
  const seconds = elapsedSeconds(fromTimestamp, now);
  return seconds === null ? null : formatElapsed(seconds);
}

/**
 * The server-derived recording states (ADR 0001 §D1). The backend is the single
 * source of truth — React never classifies, it only renders the pushed `state`.
 */
export type RecordingState = 'recording' | 'paused' | 'stalled' | 'connecting';

/**
 * Server-pushed status object, additive on each `active_servers[]` entry
 * (ADR 0001 §"Additive WS contract"). All timestamps are server-authoritative
 * UTC ISO-8601; `since` is the count-up anchor per the D1 table.
 */
export interface RecordingStatus {
  state: RecordingState | string;
  since?: string | null;
  last_transcription_at?: string | null;
  paused_since?: string | null;
}

/**
 * Presentation tone for a status — drives the chip's color treatment in the
 * tile. Kept abstract (not Tailwind classes) so this stays a pure, testable
 * mapping; the component owns the class lookup.
 *
 *   healthy    — recording / connecting (calm, timer climbing)
 *   paused     — emphasized headline (forgot-to-unpause is the thing to catch)
 *   stalled    — alarming / distinct (mic drop / connectivity loss)
 */
export type StatusTone = 'healthy' | 'paused' | 'stalled';

export interface StatusReadout {
  /** Full label incl. live elapsed, e.g. "Paused · 24:11" / "Recording · 0:08 ago". */
  label: string;
  tone: StatusTone;
  /** True for the paused headline state — the component emphasizes it. */
  emphasized: boolean;
  /** True for stalled — the component renders it alarming/distinct. */
  alarming: boolean;
}

/** Title-case an unknown future state string for forward-compatible rendering. */
function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Build the tile's status readout from the pushed `state` + count-up anchor.
 *
 * Pure: formats only, classifies nothing (AC6). `now` should be the de-skewed
 * server-equivalent time (`clientNow − offset`, where `offset = clientNow −
 * as_of`) so the count-up tracks server wall-clock regardless of device skew
 * (ADR 0001 §D4).
 *
 * Labels (spec §"User-visible behavior"):
 *   recording  → "Recording · {m:ss} ago"   (freshness since last transcription)
 *   paused     → "Paused · {m:ss}"           (emphasized)
 *   stalled    → "Stalled · {m:ss}"          (alarming)
 *   connecting → "Connecting · {m:ss}"       (transient warm-up; healthy tone, timer climbs)
 *
 * When the anchor is absent/unparseable the timer is dropped (bare label) so a
 * sparse payload still renders a meaningful chip rather than a broken one.
 * Unknown future states render their title-cased name with a healthy tone so a
 * newer backend never breaks an older tile.
 */
export function buildStatusReadout(
  state: RecordingState | string,
  since: string | null | undefined,
  now: Date | number = Date.now(),
): StatusReadout {
  const elapsed = formatElapsedSince(since, now);
  switch (state) {
    case 'paused':
      return {
        label: elapsed ? `Paused · ${elapsed}` : 'Paused',
        tone: 'paused',
        emphasized: true,
        alarming: false,
      };
    case 'stalled':
      return {
        label: elapsed ? `Stalled · ${elapsed}` : 'Stalled',
        tone: 'stalled',
        emphasized: false,
        alarming: true,
      };
    case 'connecting':
      // Transient (<30s warm-up) — same healthy affordance as recording, with a
      // climbing timer since connect. Falls back to the bare ellipsis label when
      // the anchor is absent.
      return {
        label: elapsed ? `Connecting · ${elapsed}` : 'Connecting…',
        tone: 'healthy',
        emphasized: false,
        alarming: false,
      };
    case 'recording':
      return {
        label: elapsed ? `Recording · ${elapsed} ago` : 'Recording',
        tone: 'healthy',
        emphasized: false,
        alarming: false,
      };
    default:
      // Forward-compat: render an unknown future state rather than break the tile.
      return { label: titleCase(String(state)), tone: 'healthy', emphasized: false, alarming: false };
  }
}
